import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import TopModalSection from '../common/TopModalSection';

import PromptForm, {
	PromptOptions,
} from '@/components/forms/prompt/PromptForm';
import Loader from '@/components/ui/loader/Loader';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IPrompt } from '@/interfaces/api/IPrompt';
import { ICreatePromptPayload } from '@/interfaces/api/prompt/ICreatePromptPayload';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const ViewUpdateCreatePromptModal = ({
	closeAfterSubmit,
	defaultClose,
	promptOption,
	id,
}: {
	closeAfterSubmit: () => void;
	defaultClose: () => void;
	promptOption: PromptOptions;
	id?: number;
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const {
		data: editPrompt,
		isLoading: isFetching,
		refetch,
	} = useQuery<IPrompt>({
		queryKey: [ApiQueryKeys.getPromptById, id],
		queryFn: () => haggleApiService.getPromptById(Number(id)),
		enabled:
			id !== undefined &&
			typeof id === 'number' &&
			promptOption !== PromptOptions.CREATE,
		refetchOnWindowFocus: true,
	});

	const handleCreateSubmit = async (values: ICreatePromptPayload) => {
		setIsLoading(true);
		try {
			await haggleApiService.createPrompt(values);

			notificationService.success('Prompt created successfully');
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to create a prompt: ${error}`,
				);
			}
		} finally {
			setIsLoading(false);
			closeAfterSubmit();
		}
	};

	const handleUpdateSubmit = async (values: ICreatePromptPayload) => {
		if (!id) return;

		setIsLoading(true);
		try {
			await haggleApiService.updatePrompt(Number(id), values);

			notificationService.success('Prompt updated successfully');

			refetch();
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to update a prompt: ${error}`,
				);
			}
		} finally {
			setIsLoading(false);
			closeAfterSubmit();
		}
	};

	const handleInitialValues = (
		editPrompt: IPrompt | undefined,
	): ICreatePromptPayload | undefined => {
		if (editPrompt) {
			return {
				...editPrompt,
				isDefault: editPrompt.isDefault ? 'Yes' : 'No',
			};
		}

		return undefined;
	};

	if (isFetching)
		return (
			<Loader
				variant="blink"
				className="flex items-center justify-center h-full w-full"
			/>
		);

	return (
		<div
			className="flex flex-col justify-center items-center px-4"
			data-test="view-create-prompt-modal"
		>
			<TopModalSection
				title={
					promptOption === PromptOptions.UPDATE
						? 'Update Prompt'
						: promptOption === PromptOptions.CREATE
						? 'Create new prompt'
						: 'View Prompt'
				}
				close={defaultClose}
			/>

			<PromptForm
				handleSubmit={
					editPrompt && promptOption === PromptOptions.UPDATE
						? handleUpdateSubmit
						: handleCreateSubmit
				}
				loading={isLoading || isFetching}
				initialValues={handleInitialValues(editPrompt)}
				promptOption={
					editPrompt && promptOption === PromptOptions.UPDATE
						? PromptOptions.UPDATE
						: promptOption === PromptOptions.CREATE
						? PromptOptions.CREATE
						: PromptOptions.VIEW
				}
			/>
		</div>
	);
};

export default ViewUpdateCreatePromptModal;
